import React, { useEffect } from "react";

import { ErrorBoundary } from "@sentry/react";
import { flatten } from "lodash/fp";
import {
  Navigate,
  Outlet,
  Route,
  RouteProps,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { CssBaseline, Stack } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

import AccessRedirect from "@app/analytics/AccessRedirect";
import FreeTrialActivatedModal from "@app/analytics/account/components/FreeTrialActivatedModal";
import SecureSignin from "@app/analytics/auth/SecureSignin";
import SigninCard from "@app/analytics/auth/SigninCard";
import SignupCard from "@app/analytics/auth/SignupCard";
import Calendar from "@app/analytics/Calendar";
import { BasicLayout, SideNavLayout } from "@app/analytics/components/Layout";
import ErrorPage from "@app/analytics/errors/ErrorPage";
import useReadAdmin from "@app/analytics/hooks/useReadAdmin";
import ReportsRoutes from "@app/analytics/MeetingReports";
import SingleCalendarConnect from "@app/analytics/onboarding/SingleCalendarConnect";
import { DailyReadContextProvider } from "@app/analytics/providers/DailyReadContext";
import Tutorials from "@app/analytics/TutorialsAndPopups";
import SmartScheduleFirstTimeUseTutorial from "@app/analytics/TutorialsAndPopups/SmartScheduleFirstTimeUseTutorial";
import WorkspaceOverview from "@app/analytics/WorkspaceOverview";
import useAuth from "@app/common/auth/useAuth";
import ZendeskWidget from "@app/common/components/ZendeskWidget";
import Toasts from "@app/common/containers/Toasts";
import useApps from "@app/common/hooks/useApps";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useRefreshTokenFailed from "@app/common/hooks/useRefreshTokenFailed";
import useZoomRunningContextQuery from "@app/common/hooks/useZoomRunningContextQuery";
import { BillingPlanSubscriptionContextProvider } from "@app/common/providers/BillingPlanSubscriptionContext";
import ROUTES from "@app/common/routes";
import { getRedirectTo, withQueryParams } from "@app/common/utils";
import logger from "@app/utils/logger";
import { getAppUrl } from "@app/utils/utils";

import FullPageLoadingSpinnerWithText from "@rui/FullPageLoadingSpinnerWithText";

import SettingsRoutes from "./account/SettingsRoutes";
import SingleSignOn from "./account/UserPreferences/AccountModule/SingleSignOn";
import AddRead from "./AddRead/AddRead";
import Admin from "./Admin";
import AuthLanding from "./auth/AuthLanding";
import AuthReturn from "./auth/AuthReturn";
import EmailSentSuccess from "./auth/EmailSentSuccess";
import ForgotPassword from "./auth/ForgotPassword";
import ResetPassword from "./auth/ResetPassword";
import Signout from "./auth/Signout";
import applyLangFixIfNeeded from "./browserLangFix";
import Coaching from "./Coaching";
import DailyRead from "./DailyRead";
import ExperimentProvider from "./experiments/ExperimentProvider";
import HomePage from "./Home";
import Integrations from "./Integrations";
import TeamsAdminEnable from "./Integrations/TeamsAdminEnable";
import MeetingPolicy from "./MeetingPolicy";
import MeetingReportAccessControl from "./MeetingReportAccessControl";
import Welcome from "./onboarding/Welcome";
import { BillingContextProvider } from "./providers/BillingContext";
import { FeatureAccess } from "./providers/FeatureAccessContext";
import { RollupContextProvider } from "./providers/RollupContext";
import Pulse from "./Pulse";
import Recommendations from "./Recommendations";
import ReportFileDownload from "./ReportFileDownload";
import RouteMigrations from "./RouteMigrations";
import theme from "./theme";
import TopicReport from "./TopicReport";

// Patch dom operations to make
// React play nicely with translation tools
// We'll only apply the patch if the user's browser
// language is set to something other than English
// otherwise we'll set the html tag to translate=no
applyLangFixIfNeeded();

const ShowTutorials = () => {
  const { user } = useAuth();
  const [queryParams] = useSearchParams();

  // there aren't any tutorials we want to show to non-logged-in users
  if (!user) return <Outlet />;

  // We should eventually have some kind of modal controller
  // that coordinates tutorials, alerts, popups, etc
  return (
    <>
      <FreeTrialActivatedModal />
      <SmartScheduleFirstTimeUseTutorial />
      {queryParams.get("popup") ? null : <Tutorials />}
      <Outlet />
    </>
  );
};

type AccessRouteProps = {
  accessCheck: keyof FeatureAccess | (keyof FeatureAccess)[];
  redirectTo?: string;
} & RouteProps;

const FeatureAccessCheck = ({
  accessCheck,
  redirectTo,
}: {
  accessCheck: AccessRouteProps["accessCheck"];
  redirectTo?: AccessRouteProps["redirectTo"];
}) => {
  const featureAccess = useFeatureAccess();
  const accessChecks: (keyof FeatureAccess)[] = flatten([accessCheck]);
  const hasAccess = accessChecks.every((c) => featureAccess[c]);
  return (
    <AccessRedirect hasAccess={hasAccess} redirectTo={redirectTo}>
      <Outlet />
    </AccessRedirect>
  );
};

// This is a function instead of a function component because all <Routes>
// children have to be <Route> components
const renderAccessRoute = ({
  accessCheck,
  redirectTo,
  ...props
}: AccessRouteProps) => {
  return (
    <Route
      element={
        <FeatureAccessCheck accessCheck={accessCheck} redirectTo={redirectTo} />
      }
    >
      <Route {...props} />
    </Route>
  );
};

// Admin Routes
const RequireAdminUser = () => {
  const auth = useAuth();
  const { isAdmin } = useReadAdmin();

  const hasImpersonatePermissions = auth.user && isAdmin;

  if (hasImpersonatePermissions) {
    return <Outlet />;
  }

  return <Navigate to={ROUTES.LANDING_PAGE} />;
};

// Routes that require a logged-in user
const RequireUser = () => {
  const auth = useAuth();
  const location = useLocation();

  if (auth.user) {
    return <Outlet />;
  }

  return (
    <Navigate
      to={withQueryParams(ROUTES.SIGN_IN, {
        redirectTo: getAppUrl(`${location.pathname}${location.search}`),
      })}
    />
  );
};

// Redirects to redirectTo or default landing page if
// user is logged in
const NoUser = () => {
  const auth = useAuth();
  const [queryParams] = useSearchParams();

  if (auth.user) {
    const redirectTo = getRedirectTo(queryParams);

    if (redirectTo && redirectTo.startsWith("http")) {
      logger.debug("Redirecting via href to: ", redirectTo);
      window.location.href = redirectTo;
      return <FullPageLoadingSpinnerWithText />;
    }

    return <Navigate to={redirectTo ?? ROUTES.LANDING_PAGE} replace />;
  }

  return (
    <>
      <Toasts />
      <Outlet />
    </>
  );
};

// Sign the user out before rendering the route
const EnsureNoUser = () => {
  const auth = useAuth();

  useEffect(() => {
    if (auth.user) {
      auth.signOut(false);
    }
  }, [auth.user]);

  if (auth.user) {
    return <FullPageLoadingSpinnerWithText />;
  }

  return (
    <>
      <Toasts />
      <Outlet />
    </>
  );
};

const AnalyticsProviders = () => {
  return (
    <BillingPlanSubscriptionContextProvider>
      <BillingContextProvider>
        <RollupContextProvider>
          <DailyReadContextProvider>
            <ExperimentProvider>
              <Outlet />
            </ExperimentProvider>
          </DailyReadContextProvider>
        </RollupContextProvider>
      </BillingContextProvider>
    </BillingPlanSubscriptionContextProvider>
  );
};

// Handles changes to where the app is being run. In this case,
// we are handling when the context switches from inMainClient
// to inMeeting so we can redirect to the Meeting Tools experience
const useRunningContextChange = () => {
  const { client } = useApps();

  const { runningContext } = useZoomRunningContextQuery();

  useEffect(() => {
    if (client !== "zoom") return;

    if (runningContext === "inMeeting") {
      window.location.href = getAppUrl(
        `${ROUTES.MEETING_TOOLS_HOME}?client=zoom`
      );
    }
  }, [client, runningContext]);

  return null;
};

const Analytics = () => {
  useRefreshTokenFailed();
  useRunningContextChange();

  const { canAccessHomePage } = useFeatureAccess();

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <ErrorBoundary fallback={<ErrorPage type="crash" />}>
          <ZendeskWidget />
          <CssBaseline />
          {/* relative position for emblem background */}
          <Stack style={{ position: "relative", minHeight: "100vh" }}>
            <Routes>
              <Route path="secure_signin" element={<SecureSignin />} />
              {/** signed-out user routes */}
              <Route element={<NoUser />}>
                <Route element={<AuthLanding />}>
                  <Route path="signin" element={<SigninCard />} />
                  <Route path="signup" element={<SignupCard />} />
                </Route>
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route
                  path="forgot-password/success"
                  element={<EmailSentSuccess />}
                />
              </Route>

              {/** routes that must be accessed by a signed out user */}
              <Route element={<EnsureNoUser />}>
                <Route path="reset-password" element={<ResetPassword />} />
              </Route>

              {/** utility routes */}
              <Route path="signout" element={<Signout />} />
              <Route path="oauth_return" element={<AuthReturn />} />

              {/** auth'd routes that don't need the analytics providers */}
              <Route element={<RequireUser />}>
                <Route element={<BasicLayout />}>
                  <Route
                    path="sso/calendar/:providerId"
                    element={<SingleCalendarConnect />}
                  />
                </Route>
              </Route>

              <Route element={<AnalyticsProviders />}>
                <Route element={<BasicLayout />}>
                  {/** Basic Layout - Authenticated only */}
                  <Route element={<RequireUser />}>
                    <Route path="welcome/:step?" element={<Welcome />} />
                    <Route path="sso/add" element={<SingleSignOn />} />
                    {renderAccessRoute({
                      accessCheck: ["canAccessTranscript"],
                      path: "meeting-file-download/:sessionId/:reportType",
                      element: <ReportFileDownload />,
                    })}
                    <Route
                      path="topic-file-download/:topicId"
                      element={<ReportFileDownload />}
                    />
                    {/* Daily Read without an ID specified is for the current user's latest Daily Read.
                        User must be logged in to view  */}
                    <Route path="daily-read" element={<DailyRead />} />
                    <Route path="add" element={<AddRead />} />
                  </Route>

                  {/** Basic Layout - Authenticated or unauthenticated */}
                  <Route
                    path="teams-integration/:token"
                    element={<TeamsAdminEnable />}
                  />
                  {/* Daily Read with an ID specified could be anyone's Daily Read; don't need to be logged in to view */}
                  <Route path="daily-read/:id" element={<DailyRead />} />
                </Route>

                <Route element={<SideNavLayout />}>
                  {/** Side Nav Layout - Admin only */}
                  <Route element={<RequireAdminUser />}>
                    <Route path="admin" element={<Admin />} />
                  </Route>

                  <Route element={<ShowTutorials />}>
                    {/** Side Nav Layout - Authenticated or unauthenticated */}
                    <Route
                      path="meetings/:sessionId/*"
                      element={<MeetingReportAccessControl />}
                    />

                    {/** Side Nav Layout - Authenticated only */}
                    <Route element={<RequireUser />}>
                      {canAccessHomePage && (
                        <Route path="home/*" element={<HomePage />} />
                      )}
                      <Route path="for-you/*" element={<Pulse />} />
                      {renderAccessRoute({
                        path: "workspace/:workspaceId/overview",
                        element: <WorkspaceOverview />,
                        accessCheck: "canAccessWorkspaceOverview",
                      })}
                      <Route
                        path="meeting-policy"
                        element={<MeetingPolicy />}
                      />
                      <Route
                        path="workspace/:workspaceId/meeting-policy"
                        element={<MeetingPolicy />}
                      />
                      <Route path="reports/*" element={<ReportsRoutes />} />
                      <Route path="topics/:topicId" element={<TopicReport />} />
                      {renderAccessRoute({
                        accessCheck: "canAccessCoaching",
                        path: "coaching/*",
                        element: <Coaching />,
                      })}
                      <Route
                        path="recommendations/:tabId?"
                        element={<Recommendations />}
                      />
                      <Route path="calendar/*" element={<Calendar />} />
                      <Route path="integrations/*" element={<Integrations />} />
                      <Route path="settings/*" element={<SettingsRoutes />} />
                    </Route>
                  </Route>
                </Route>
              </Route>

              {/** Utility routes */}
              <Route
                index
                element={<Navigate to={ROUTES.LANDING_PAGE} replace />}
              />
              <Route path="404" element={<ErrorPage type="not_found" />} />
              <Route path="500" element={<ErrorPage type="server_crash" />} />
              <Route path="*" element={<RouteMigrations />} />
            </Routes>
          </Stack>
        </ErrorBoundary>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default Analytics;
